/**
 * @file index.tsx
 *
 * @fileoverview Renders large organization teaser.
 * The component is designed to be displayed on landing pages and blog
 * detail pages. It emphasizes information about the company, the UI
 * occupies full parent width and appears large on the screen, hence the name.
 */
import Grid from "../../../02-atoms/06-layout/04-grid";
import Cell from "../../../02-atoms/06-layout/04-grid/01-cell";
import { ReactNode } from "react";
import FlexItems from "../../../02-atoms/06-layout/03-flex";
import FlexItem from "../../../02-atoms/06-layout/03-flex/02-flex-item";
import Text from "../../../02-atoms/13-text/03-text";
import styles from "./organizationTeaserLarge.module.scss";
import Box from "../../../02-atoms/07-box";

export interface OrganizationTeaserLargeField {
  Icon: ReactNode;
  Label?: string | ReactNode;
}

interface OrganizationTeaserLargeProps {
  /**
   * The teaser title.
   */
  title?: string;
  /**
   * The teaser title tag.
   *
   * @default "h2"
   */
  titleTag?: string;
  /**
   * The teaser subtitle.
   */
  subtitle?: string;
  /**
   * The organization title.
   */
  Name: ReactNode;
  /**
   * The banner for the organization.
   */
  Banner?: ReactNode;
  /**
   * The logo for the organization.
   */
  Logo?: ReactNode;
  /**
   * Fields that will be shown bellow the name.
   */
  fields: OrganizationTeaserLargeField[];
  /**
   * The none box shadow flag.
   *
   * @default false
   */
  boxShadowNone?: boolean;
}

export function OrganizationTeaserLarge({
  title,
  titleTag = "h2",
  subtitle,
  Name,
  Banner,
  Logo,
  fields,
  boxShadowNone = false,
}: OrganizationTeaserLargeProps) {
  return (
    <Box padding={0} {...(boxShadowNone && { boxShadow: "none" })}>
      <Grid
        columns={[1, Banner ? 2 : 1, Banner ? 2 : 1]}
        alignItems="center"
        gap={4}
      >
        <Cell
          className={styles.cell}
          gridRow={[2, 1]}
          paddingLeft={[6, 8]}
          paddingRight={[6, 8]}
          paddingBottom={[6, 8]}
          paddingTop={[6, 8]}
        >
          <FlexItems flexDirection="column">
            {title && (
              <FlexItem>
                <Text
                  tag={titleTag}
                  fontSize={[5, 6]}
                  margin={0}
                  marginTop={[3, 5]}
                >
                  {title}
                </Text>
              </FlexItem>
            )}
            {subtitle && (
              <FlexItem>
                <Text
                  fontSize={[3, 4]}
                  marginTop={[3, 5]}
                  marginBottom={[4, 6]}
                >
                  {subtitle}
                </Text>
              </FlexItem>
            )}
            {Logo && <FlexItem>{Logo}</FlexItem>}
            <FlexItem>
              <Text fontSize={4} marginBottom={4}>
                {Name}
              </Text>
            </FlexItem>
          </FlexItems>
          <FlexItems flexDirection="column" gap={3}>
            {fields.map(
              ({ Icon, Label }, index) =>
                Label && (
                  <FlexItems key={index} alignItems="center" flexWrap="nowrap">
                    <FlexItem>{Icon}</FlexItem>
                    <FlexItem>
                      <Text
                        color={"gray4"}
                        margin="0"
                        marginLeft={4}
                        fontSize={3}
                      >
                        {Label}
                      </Text>
                    </FlexItem>
                  </FlexItems>
                )
            )}
          </FlexItems>
        </Cell>
        {Banner && (
          <Cell className={styles.bannerCell} gridRow={[1]}>
            {Banner}
          </Cell>
        )}
      </Grid>
    </Box>
  );
}
export default OrganizationTeaserLarge;
